/**
 * PostHog wrapper for the caregiver app (caregiver.kinbridgehealth.com).
 *
 * The snippet in public/index.html loads PostHog into `window.posthog`
 * (KinBridge project, US cloud). This module is the only place app code
 * should touch it.
 *
 * Privacy posture — this app holds PHI, so:
 *  - COOKIELESS: persistence "memory". No analytics cookie, nothing survives
 *    the tab. Cross-session stitching happens via identify(user.id) instead.
 *  - autocapture OFF, session replay OFF, surveys OFF, heatmaps OFF.
 *  - Users are identified by their opaque user id only. Never email, name,
 *    the care recipient's name, DOB, conditions, claim text, meds or vitals.
 *  - Event properties are limited to labels: plan id, step names, booleans.
 *  - Route paths with tokens/ids are scrubbed before they leave the browser.
 */

const ph = () => (typeof window !== "undefined" ? window.posthog : undefined);

/** `/activate/abc123` -> `/activate/:token`, `/patient-invite/xyz` -> `/patient-invite/:token`. */
export function scrubPath(pathname) {
  return pathname
    .replace(/^\/activate\/[^/]+/, "/activate/:token")
    .replace(/^\/patient-invite\/[^/]+/, "/patient-invite/:token");
}

/** Called on every client-side route change (PostHog's own pageview capture is off). */
export function capturePageview(pathname) {
  const p = ph();
  if (!p) return;
  const path = scrubPath(pathname);
  // Keep utm_* / gclid / fbclid so paid traffic from /pilot attributes correctly;
  // drop everything else (promo codes, tokens).
  const params = new URLSearchParams(window.location.search);
  const keep = new URLSearchParams();
  params.forEach((v, k) => { if (/^(utm_|gclid$|fbclid$)/.test(k)) keep.set(k, v); });
  const qs = keep.toString();
  try {
    p.capture("$pageview", {
      $current_url: `${window.location.origin}${path}${qs ? `?${qs}` : ""}`,
      $pathname: path,
    });
  } catch { /* analytics must never break the app */ }
}

export function captureEvent(name, props = {}) {
  try { ph()?.capture(name, props); } catch { /* no-op */ }
}

/** Identify by opaque user id + non-PHI labels only. */
export function identifyUser(user, extra = {}) {
  if (!user?.id) return;
  try {
    ph()?.identify(user.id, {
      kinbridge_role: user.company_id ? (user.employer_covered ? "employer_covered_caregiver" : "employer_admin") : "caregiver",
      company_id: user.company_id || undefined,
      employer_covered: !!user.employer_covered,
      ...extra,
    });
  } catch { /* no-op */ }
}

export function resetUser() {
  try { ph()?.reset(); } catch { /* no-op */ }
}
